import { Controller } from "@hotwired/stimulus";

// A small, self-contained searchable single-select. Keeps a real hidden field
// as the form value (reliable submission) and layers a filterable popover list
// on top: type to filter, click / Enter to choose, arrows to navigate, Esc or
// outside-click to dismiss.
//
// NOTE: Primer's Alpha::SelectPanel does support form inputs (form_arguments on
// the local fetch strategy), so prefer it for new pickers; this control should
// be migrated to it rather than extended.
//
// Targets: field (hidden input), trigger (button), label (text in trigger),
// panel, search (filter input), option (one button per choice).
export class ComboboxController extends Controller {
  static targets = ["field", "trigger", "label", "panel", "search", "option"];

  connect() {
    this.onDocClick = (event) => {
      if (!this.element.contains(event.target)) this.close();
    };
    document.addEventListener("click", this.onDocClick);
    this.activeIndex = -1;
  }

  disconnect() {
    document.removeEventListener("click", this.onDocClick);
  }

  toggle(event) {
    event.preventDefault();
    this.panelTarget.hidden ? this.open() : this.close();
  }

  open() {
    this.panelTarget.hidden = false;
    this.triggerTarget.setAttribute("aria-expanded", "true");
    this.positionPanel();
    if (this.hasSearchTarget) {
      this.searchTarget.value = "";
      this.filter();
      this.searchTarget.focus();
    }
  }

  // Flip the panel above the trigger when there isn't room below (e.g. the
  // composer sits at the bottom of the page).
  positionPanel() {
    this.element.classList.remove("is-above");
    const trigger = this.triggerTarget.getBoundingClientRect();
    const panelHeight = this.panelTarget.offsetHeight;
    const spaceBelow = window.innerHeight - trigger.bottom;
    if (spaceBelow < panelHeight && trigger.top > spaceBelow) {
      this.element.classList.add("is-above");
    }
  }

  close() {
    this.panelTarget.hidden = true;
    this.triggerTarget.setAttribute("aria-expanded", "false");
    this.clearActive();
    this.activeIndex = -1;
  }

  filter() {
    const query = this.searchTarget.value.trim().toLowerCase();
    this.optionTargets.forEach((option) => {
      option.hidden = query.length > 0 && !option.textContent.toLowerCase().includes(query);
    });
    this.clearActive();
    this.activeIndex = -1;
  }

  select(event) {
    const option = event.currentTarget;
    this.fieldTarget.value = option.dataset.value;
    this.labelTarget.textContent = option.textContent.trim();
    this.optionTargets.forEach((o) => o.setAttribute("aria-selected", o === option ? "true" : "false"));
    this.close();
    this.triggerTarget.focus();
  }

  keydown(event) {
    const options = this.visibleOptions;
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.move(1, options);
        break;
      case "ArrowUp":
        event.preventDefault();
        this.move(-1, options);
        break;
      case "Enter":
        event.preventDefault();
        if (options[this.activeIndex]) options[this.activeIndex].click();
        break;
      case "Escape":
        this.close();
        this.triggerTarget.focus();
        break;
    }
  }

  get visibleOptions() {
    return this.optionTargets.filter((option) => !option.hidden);
  }

  clearActive() {
    this.optionTargets.forEach((option) => option.classList.remove("is-active"));
    if (this.hasSearchTarget) this.searchTarget.removeAttribute("aria-activedescendant");
  }

  move(direction, options) {
    if (!options.length) return;
    this.clearActive();
    this.activeIndex = (this.activeIndex + direction + options.length) % options.length;
    const active = options[this.activeIndex];
    active.classList.add("is-active");
    if (this.hasSearchTarget) this.searchTarget.setAttribute("aria-activedescendant", active.id);
    active.scrollIntoView({ block: "nearest" });
  }
}
